// @ts-nocheck
import { Cartridge } from "./cartridge.js";
import { CartridgeType } from "./cartridge-type.enum.js";
var Mbc1WriteType;
(function (Mbc1WriteType) {
    Mbc1WriteType[Mbc1WriteType["RamGateRegister"] = 0] = "RamGateRegister";
    Mbc1WriteType[Mbc1WriteType["Bank1Register"] = 1] = "Bank1Register";
    Mbc1WriteType[Mbc1WriteType["Bank2Register"] = 2] = "Bank2Register";
    Mbc1WriteType[Mbc1WriteType["ModeRegister"] = 3] = "ModeRegister";
    Mbc1WriteType[Mbc1WriteType["Sram"] = 4] = "Sram";
})(Mbc1WriteType || (Mbc1WriteType = {}));
var Mbc1ReadType;
(function (Mbc1ReadType) {
    Mbc1ReadType[Mbc1ReadType["RomBankZero"] = 0] = "RomBankZero";
    Mbc1ReadType[Mbc1ReadType["RomBankZeroMode1"] = 1] = "RomBankZeroMode1";
    Mbc1ReadType[Mbc1ReadType["RomBank"] = 2] = "RomBank";
    Mbc1ReadType[Mbc1ReadType["Sram"] = 3] = "Sram";
    Mbc1ReadType[Mbc1ReadType["Invalid"] = 4] = "Invalid";
})(Mbc1ReadType || (Mbc1ReadType = {}));
export class Mbc1Cartridge extends Cartridge {
    isRamEnabled = false;
    bank1 = 0b00001;
    bank2 = 0b0;
    mode = 0b0;
    ramData;
    ramDataView;
    ramBytes;
    // Used to debounce calls to on sram write callback. Allows auto saving data without
    // hammering writes
    writeTimeout;
    onSramWrite;
    constructor(gameDataView) {
        super(gameDataView);
        this.ramData = new ArrayBuffer(this.ramSize);
        this.ramDataView = new DataView(this.ramData);
        this.ramBytes = new Uint8Array(this.ramDataView.buffer);
        this.ramBytes.fill(0xff);
    }
    setRam(sramArrayBuffer) {
        this.ramData = sramArrayBuffer;
        this.ramDataView = new DataView(this.ramData);
        this.ramBytes = new Uint8Array(this.ramDataView.buffer);
    }
    dumpRam() {
        return this.ramDataView.buffer;
    }
    writeByte(address, value) {
        const sramWrite = (address, value) => {
            this.ramDataView.setUint8(address, value);
            if (this.type === CartridgeType.MBC1_RAM_BATTERY && this.onSramWrite) {
                clearTimeout(this.writeTimeout);
                this.writeTimeout = setTimeout(() => this.onSramWrite(this.ramData), 500);
            }
        };
        this.write(address, value, sramWrite);
    }
    writeWord(address, value) {
        const sramWrite = (address, value) => {
            this.ramDataView.setUint16(address, value, true);
            if (this.type === CartridgeType.MBC1_RAM_BATTERY && this.onSramWrite) {
                clearTimeout(this.writeTimeout);
                this.writeTimeout = setTimeout(() => this.onSramWrite(this.ramData), 500);
            }
        };
        this.write(address, value, sramWrite);
    }
    readByte(address) {
        const cartridgeRead = (address) => this.gameDataView.getUint8(address);
        const sramRead = (address) => this.ramDataView.getUint8(address);
        return this.read(address, cartridgeRead, sramRead);
    }
    readSignedByte(address) {
        const cartridgeRead = (address) => this.gameDataView.getInt8(address);
        const sramRead = (address) => this.ramDataView.getInt8(address);
        return this.read(address, cartridgeRead, sramRead);
    }
    readWord(address) {
        const cartridgeRead = (address) => this.gameDataView.getUint16(address, true);
        const sramRead = (address) => this.ramDataView.getUint16(address, true);
        return this.read(address, cartridgeRead, sramRead);
    }
    // Flat ROM offset for an address in the ROM window, mirroring the bank
    // math in read() exactly (MultiNostalgia patch — see ../../PATCHES.md).
    romOffsetForAddress(address) {
        const maskedAddress = address & 0b11111111111111;
        if (address >= 0x0000 && address <= 0x3fff) {
            if (this.mode === 0)
                return maskedAddress;
            const bankNumber = this.bank2 << 5;
            return ((bankNumber << 14) + maskedAddress) & (this.romSize - 1);
        }
        // $4000-$7fff: the switchable bank
        const bankNumber = (this.bank2 << 5) + this.bank1;
        return ((bankNumber << 14) + maskedAddress) & (this.romSize - 1);
    }
    read(address, readFromCartridge, readFromSram) {
        const maskedAddress = address & 0b11111111111111;
        if (address >= 0x0000 && address <= 0x3fff) {
            if (this.mode === 0) {
                return readFromCartridge(maskedAddress);
            }
            else {
                const bankNumber = this.bank2 << 5;
                const bankCorrectedAddress = (bankNumber << 14) + maskedAddress;
                const wrappedForSize = bankCorrectedAddress & (this.romSize - 1);
                return readFromCartridge(wrappedForSize);
            }
        }
        else if (address >= 0x4000 && address <= 0x7fff) {
            const bankNumber = (this.bank2 << 5) + this.bank1;
            const bankCorrectedAddress = (bankNumber << 14) + maskedAddress;
            const wrappedForSize = bankCorrectedAddress & (this.romSize - 1);
            return readFromCartridge(wrappedForSize);
        }
        else {
            if (!this.isRamEnabled) {
                return 0xff;
            }
            const maskedAddress = address & 0b1111111111111;
            if (this.mode === 0 || this.ramSize === 0x008000) {
                return readFromSram(maskedAddress);
            }
            else {
                const bankedAddress = (this.bank2 << 13) + maskedAddress;
                return readFromSram(bankedAddress);
            }
        }
    }
    write(address, value, writeToSram) {
        if (this.isRamGate(address)) {
            const valueToEnableRam = 0b1010;
            const lowerNibble = value & 0b1111;
            this.isRamEnabled = lowerNibble === valueToEnableRam;
        }
        else if (this.isBank1(address)) {
            const masked = value & 0b11111;
            this.bank1 = masked === 0 ? 1 : masked; // zero not allowed in bank 1
        }
        else if (this.isBank2(address)) {
            this.bank2 = value & 0b11;
        }
        else if (this.isMode(address)) {
            this.mode = value & 0b1;
        }
        else if (this.isRam(address) && this.isRamEnabled) {
            const maskedAddress = address & 0b1111111111111;
            if (this.mode === 0 || this.ramSize === 0x008000) {
                writeToSram(maskedAddress, value);
            }
            else {
                const bankedAddress = (this.bank2 << 13) + maskedAddress;
                writeToSram(bankedAddress, value);
            }
        }
    }
    isRamGate(address) {
        return address >= 0x0000 && address <= 0x1fff;
    }
    isBank1(address) {
        return address >= 0x2000 && address <= 0x3fff;
    }
    isBank2(address) {
        return address >= 0x4000 && address <= 0x5fff;
    }
    isMode(address) {
        return address >= 0x6000 && address <= 0x7fff;
    }
    isRam(address) {
        return address >= 0xa000 && address <= 0xbfff;
    }
}
//# sourceMappingURL=mbc1-cartridge.js.map